import type { ReactNode } from 'react';
import { create } from 'zustand';

export interface ModalConfig {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  // Optional extra class merged onto the `.modal` box (e.g. `modal--lg` for
  // wider modals that host scrollable content like Terms & Policies).
  className?: string;
  // Renders the confirm button with `.btn--danger` styling instead of primary
  // — for destructive confirmations like the account-withdrawal request.
  confirmDanger?: boolean;
}

interface ModalStore {
  modal: ModalConfig | null;
  open: (config: ModalConfig) => void;
  close: () => void;
}

export const useModalStore = create<ModalStore>((set) => ({
  modal: null,
  open: (config) => set({ modal: config }),
  close: () => set({ modal: null }),
}));

// Global convenience API matching prototype's `modal.alert(...)` / `modal.confirm(...)` style.
export const modal = {
  alert: (title: string, body: ReactNode, onClose?: () => void) => {
    useModalStore.getState().open({
      title,
      body,
      onConfirm: () => {
        onClose?.();
        useModalStore.getState().close();
      },
    });
  },
  confirm: (title: string, body: ReactNode, onConfirm: () => void) => {
    useModalStore.getState().open({
      title,
      body,
      showCancel: true,
      onConfirm: () => {
        onConfirm();
        useModalStore.getState().close();
      },
      onCancel: () => useModalStore.getState().close(),
    });
  },
};
